import { useCallback, useEffect, useState } from "react";

import RightTimeApi from "@/API";

import { toast } from "react-toastify";

import { FaPlusSquare } from "react-icons/fa";

import { MdCheckBox, MdCheckBoxOutlineBlank, MdDelete } from "react-icons/md";

import {

  getLanguagePickerLabel,

  getLanguageRegistryName,

  type LanguageLike,

} from "@/utils/languageUtils";

import { useI18n } from "@/i18n/I18nProvider";

import Enums from "@/enums";

import SelectModal from "../elements/modals/SelectModal";

import { apiMessage } from "@/utils/apiMessage";



interface AdminLanguagesTabProps {

  tabData?: {

    activeTab?: string;

    updateSpinner?: (show: boolean) => void;

    adminPerms?: number[];

  };

}



type TranslatorLanguageOption = {

  code: string;

  name: string;

  nativeName: string;

  label: string;

  Code?: string;

  Name?: string;

  NativeName?: string;

  Label?: string;

};



const ENGLISH_LANGUAGE_ID = 1;



function readBooleanFlag(raw: Record<string, unknown>, key: string, pascalKey: string): boolean {

  const value = raw[key] ?? raw[pascalKey];

  return value === true;

}



function normalizeLanguage(raw: LanguageLike & {

  missingSlotCount?: number | null;

  MissingSlotCount?: number | null;

  pendingTranslationCount?: number | null;

  PendingTranslationCount?: number | null;

  untranslatedSlotCount?: number | null;

  UntranslatedSlotCount?: number | null;

  allocatedSlotCount?: number | null;

  AllocatedSlotCount?: number | null;

  translatedSlotCount?: number | null;

  TranslatedSlotCount?: number | null;

  missingFieldTranslationCount?: number | null;

  MissingFieldTranslationCount?: number | null;

  remainingMissingSlotCount?: number | null;

  RemainingMissingSlotCount?: number | null;

  language?: LanguageLike & { missingSlotCount?: number | null; MissingSlotCount?: number | null };

  Language?: LanguageLike & { missingSlotCount?: number | null; MissingSlotCount?: number | null };

}) {

  const record = (raw?.language ?? raw?.Language ?? raw) as LanguageLike & {

    missingSlotCount?: number | null;

    MissingSlotCount?: number | null;

    untranslatedSlotCount?: number | null;

    UntranslatedSlotCount?: number | null;

    allocatedSlotCount?: number | null;

    AllocatedSlotCount?: number | null;

    translatedSlotCount?: number | null;

    TranslatedSlotCount?: number | null;

    missingFieldTranslationCount?: number | null;

    MissingFieldTranslationCount?: number | null;

    pendingTranslationCount?: number | null;

    PendingTranslationCount?: number | null;

  };

  const recordObj = record as Record<string, unknown>;

  const missingSlotCountRaw = record?.missingSlotCount

    ?? record?.MissingSlotCount

    ?? raw?.remainingMissingSlotCount

    ?? raw?.RemainingMissingSlotCount

    ?? raw?.missingSlotCount

    ?? raw?.MissingSlotCount;

  const pendingTranslationCountRaw = record?.pendingTranslationCount

    ?? record?.PendingTranslationCount

    ?? raw?.pendingTranslationCount

    ?? raw?.PendingTranslationCount;

  const untranslatedSlotCountRaw = record?.untranslatedSlotCount

    ?? record?.UntranslatedSlotCount

    ?? raw?.untranslatedSlotCount

    ?? raw?.UntranslatedSlotCount;

  const allocatedSlotCountRaw = record?.allocatedSlotCount

    ?? record?.AllocatedSlotCount

    ?? raw?.allocatedSlotCount

    ?? raw?.AllocatedSlotCount;

  const translatedSlotCountRaw = record?.translatedSlotCount

    ?? record?.TranslatedSlotCount

    ?? raw?.translatedSlotCount

    ?? raw?.TranslatedSlotCount;

  const missingFieldTranslationCountRaw = record?.missingFieldTranslationCount

    ?? record?.MissingFieldTranslationCount

    ?? raw?.missingFieldTranslationCount

    ?? raw?.MissingFieldTranslationCount;

  const missingSlotCount = missingSlotCountRaw == null ? null : Number(missingSlotCountRaw);

  const pendingTranslationCount = pendingTranslationCountRaw == null ? null : Number(pendingTranslationCountRaw);

  const untranslatedSlotCount = untranslatedSlotCountRaw == null ? null : Number(untranslatedSlotCountRaw);

  const allocatedSlotCount = allocatedSlotCountRaw == null ? null : Number(allocatedSlotCountRaw);

  const translatedSlotCount = translatedSlotCountRaw == null ? null : Number(translatedSlotCountRaw);

  const missingFieldTranslationCount = missingFieldTranslationCountRaw == null
    ? null
    : Number(missingFieldTranslationCountRaw);



  return {

    id: Number(record?.id ?? 0),

    name: String(record?.name ?? ""),

    display: String(record?.display || record?.Display || record?.name || "").trim(),

    abbrev: String(record?.abbrev ?? ""),

    ietf: String(record?.ietf ?? record?.Ietf ?? record?.IETF ?? ""),

    supported: readBooleanFlag(recordObj, "supported", "Supported"),

    active: readBooleanFlag(recordObj, "active", "Active"),

    missingSlotCount: Number.isFinite(missingSlotCount) ? missingSlotCount : null,

    pendingTranslationCount: Number.isFinite(pendingTranslationCount) ? pendingTranslationCount : null,

    untranslatedSlotCount: Number.isFinite(untranslatedSlotCount) ? untranslatedSlotCount : null,

    allocatedSlotCount: Number.isFinite(allocatedSlotCount) ? allocatedSlotCount : null,

    translatedSlotCount: Number.isFinite(translatedSlotCount) ? translatedSlotCount : null,

    missingFieldTranslationCount: Number.isFinite(missingFieldTranslationCount)
      ? missingFieldTranslationCount
      : null,

  };

}



function sortLanguagesByName(languages: ReturnType<typeof normalizeLanguage>[]) {

  return [...languages].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

}



function hasAllocatedContentSlots(language: ReturnType<typeof normalizeLanguage>) {

  if (language.id <= ENGLISH_LANGUAGE_ID) return true;

  if (language.allocatedSlotCount != null && language.allocatedSlotCount > 0) return true;

  if ((language.pendingTranslationCount ?? 0) > 0) return true;

  if ((language.untranslatedSlotCount ?? 0) > 0) return true;

  if (language.supported && language.missingSlotCount === 0) return true;

  return false;

}



function canDeleteLanguage(language: ReturnType<typeof normalizeLanguage>) {

  if (language.id <= ENGLISH_LANGUAGE_ID) return false;

  return !hasAllocatedContentSlots(language);

}



function getTranslationWorkCount(language: ReturnType<typeof normalizeLanguage>) {

  return (language.missingSlotCount ?? 0)

    + (language.untranslatedSlotCount ?? 0)

    + (language.missingFieldTranslationCount ?? 0);

}



function languageNeedsTranslationWork(language: ReturnType<typeof normalizeLanguage>) {

  return getTranslationWorkCount(language) > 0;

}



function shouldShowProvisionButton(language: ReturnType<typeof normalizeLanguage>) {

  if (language.id <= ENGLISH_LANGUAGE_ID || !language.supported) return false;

  return language.missingSlotCount != null && language.missingSlotCount > 0;

}



function shouldShowTranslateButton(language: ReturnType<typeof normalizeLanguage>) {

  if (language.id <= ENGLISH_LANGUAGE_ID || !language.supported) return false;

  if ((language.pendingTranslationCount ?? 0) > 0) return false;

  return languageNeedsTranslationWork(language);

}



function shouldShowResetTranslationsButton(language: ReturnType<typeof normalizeLanguage>) {

  if (language.id <= ENGLISH_LANGUAGE_ID || !language.supported || language.active) return false;

  return (language.translatedSlotCount ?? 0) > 0;

}



function shouldShowAllocatedStatus(language: ReturnType<typeof normalizeLanguage>) {

  if (!language.supported) return false;

  if (shouldShowProvisionButton(language) || shouldShowTranslateButton(language)) return false;

  if ((language.pendingTranslationCount ?? 0) > 0) return false;

  return (language.allocatedSlotCount ?? 0) > 0;

}



function getSlotsCellTitle(language: ReturnType<typeof normalizeLanguage>) {

  if (language.id === ENGLISH_LANGUAGE_ID) {

    return "English is the source language for all content slots";

  }

  if (!language.supported) {

    return "Enable Supported to allocate content slots and queue translation";

  }

  if (shouldShowProvisionButton(language)) {

    return language.missingSlotCount == null

      ? "Create content slots and queue translation"

      : `${language.missingSlotCount} content slot(s) still need to be created; Provision will queue translation`;

  }

  if (shouldShowTranslateButton(language)) {

    const untranslated = language.untranslatedSlotCount ?? 0;

    const missing = language.missingSlotCount ?? 0;

    const missingFields = language.missingFieldTranslationCount ?? 0;

    const parts: string[] = [];

    if (missing > 0) parts.push(`${missing} content slot(s) still need to be created`);

    if (untranslated > 0) parts.push(`${untranslated} content slot(s) ready for translation`);

    if (missingFields > 0) parts.push(`${missingFields} field label(s) need translation`);

    return parts.length > 0

      ? `Translate will provision missing slots, translate field labels, and queue content translation. ${parts.join("; ")}.`

      : "Queue translation for this language";

  }

  if ((language.pendingTranslationCount ?? 0) > 0) {

    return `${language.pendingTranslationCount} slot(s) awaiting background translation`;

  }

  if (shouldShowAllocatedStatus(language)) {

    return "All content slots and field labels are translated for this language";

  }

  if ((language.missingSlotCount ?? 0) > 0) {

    return `${language.missingSlotCount} content slot(s) still need to be created`;

  }

  return "No content slots allocated yet";

}



function normalizeTranslatorLanguage(raw: TranslatorLanguageOption): TranslatorLanguageOption {

  const code = String(raw.code ?? raw.Code ?? "");

  const name = String(raw.name ?? raw.Name ?? code);

  const nativeName = String(raw.nativeName ?? raw.NativeName ?? name);

  const label = String(raw.label ?? raw.Label ?? (

    name === nativeName ? name : `${nativeName} (${name})`

  ));

  return { code, name, nativeName, label };

}



export default function AdminLanguagesTab(props: AdminLanguagesTabProps) {

  const activeTab = props.tabData?.activeTab;

  const updateSpinner = props.tabData?.updateSpinner;

  const adminPerms = props.tabData?.adminPerms;

  const { refreshFieldTranslations } = useI18n();



  const [languages, setLanguages] = useState<ReturnType<typeof normalizeLanguage>[]>([]);

  const [displayDrafts, setDisplayDrafts] = useState<Record<number, string>>({});

  const [loading, setLoading] = useState(false);

  const [savingLanguageId, setSavingLanguageId] = useState<number | null>(null);

  const [showAddLanguageModal, setShowAddLanguageModal] = useState(false);

  const [availableTranslatorLanguages, setAvailableTranslatorLanguages] = useState<TranslatorLanguageOption[]>([]);

  const [loadingTranslatorLanguages, setLoadingTranslatorLanguages] = useState(false);



  const isSystemAdmin = Array.isArray(adminPerms)

    && adminPerms.some((perm) => Number(perm) === Number(Enums.ROLE_PERMS.SystemAdmin));



  const isOnThisTab = activeTab === "languages";



  const loadLanguages = useCallback(async () => {

    setLoading(true);

    if (typeof updateSpinner === "function") updateSpinner(true);

    try {

      const resp = await RightTimeApi.getAsync("/Languages?all=true");

      if (resp?.ok && Array.isArray(resp.jsonData)) {

        const normalized = resp.jsonData.map((item: LanguageLike) => normalizeLanguage(item));

        setLanguages(sortLanguagesByName(normalized));

        setDisplayDrafts(

          Object.fromEntries(normalized.map((lang) => [lang.id, lang.display]))

        );

      } else {

        toast.error("Failed to load languages.");

        setLanguages([]);

        setDisplayDrafts({});

      }

    } catch (error) {

      console.error("Error loading languages:", error);

      toast.error("Error loading languages.");

      setLanguages([]);

      setDisplayDrafts({});

    } finally {

      setLoading(false);

      if (typeof updateSpinner === "function") updateSpinner(false);

    }

  }, [updateSpinner]);



  const loadAvailableTranslatorLanguages = useCallback(async () => {

    setLoadingTranslatorLanguages(true);

    try {

      const resp = await RightTimeApi.getAsync("/Languages/translator-available");

      if (resp?.ok && Array.isArray(resp.jsonData)) {

        setAvailableTranslatorLanguages(

          resp.jsonData.map((item: TranslatorLanguageOption) => normalizeTranslatorLanguage(item))

        );

      } else {

        toast.error(apiMessage(resp, "Failed to load translator languages."));

        setAvailableTranslatorLanguages([]);

      }

    } catch (error) {

      console.error("Error loading translator languages:", error);

      toast.error("Error loading translator languages.");

      setAvailableTranslatorLanguages([]);

    } finally {

      setLoadingTranslatorLanguages(false);

    }

  }, []);



  useEffect(() => {

    if (isOnThisTab && isSystemAdmin) {

      void loadLanguages();

    }

  }, [isOnThisTab, isSystemAdmin, loadLanguages]);



  async function openAddLanguageModal() {

    setShowAddLanguageModal(true);

    await loadAvailableTranslatorLanguages();

  }



  async function addLanguage(selected: unknown) {

    const option = normalizeTranslatorLanguage(selected as TranslatorLanguageOption);

    if (!option.code) return;



    setShowAddLanguageModal(false);

    if (typeof updateSpinner === "function") updateSpinner(true);

    try {

      const resp = await RightTimeApi.postAsync("/Languages", { code: option.code });

      if (resp?.ok && resp.jsonData) {

        const created = normalizeLanguage(resp.jsonData);

        toast.success(`${getLanguagePickerLabel(created)} added. Enable Supported when ready to allocate slots.`);

        await loadLanguages();

      } else {

        toast.error(apiMessage(resp, "Failed to add language."));

      }

    } catch (error) {

      console.error("Error adding language:", error);

      toast.error("Error adding language.");

    } finally {

      if (typeof updateSpinner === "function") updateSpinner(false);

    }

  }



  async function updateLanguage(

    language: ReturnType<typeof normalizeLanguage>,

    updates: { supported?: boolean; active?: boolean; display?: string }

  ) {

    const payload: { supported?: boolean; active?: boolean; display?: string } = {};



    if (updates.supported !== undefined || updates.active !== undefined) {

      let supported = updates.supported ?? language.supported;

      let active = updates.active ?? language.active;

      if (active && !supported) supported = true;

      if (!supported) active = false;

      payload.supported = supported;

      payload.active = active;

    }



    if (updates.display !== undefined) {

      const display = updates.display.trim();

      if (!display) {

        toast.error("Display cannot be empty.");

        return;

      }

      payload.display = display;

    }



    if (

      payload.supported === undefined

      && payload.active === undefined

      && payload.display === undefined

    ) {

      return;

    }



    if (

      payload.supported === language.supported

      && payload.active === language.active

      && (payload.display === undefined || payload.display === language.display)

    ) {

      return;

    }



    setSavingLanguageId(language.id);

    if (typeof updateSpinner === "function") updateSpinner(true);

    try {

      const resp = await RightTimeApi.putAsync(`/Languages/${language.id}`, payload);

      if (resp?.ok && resp.jsonData) {

        const updated = normalizeLanguage(resp.jsonData);

        setLanguages((current) => current.map((item) => (item.id === updated.id ? updated : item)));

        setDisplayDrafts((current) => ({ ...current, [updated.id]: updated.display }));

        await refreshFieldTranslations();

        await loadLanguages();

        const becameSupported = payload.supported === true && !language.supported;

        toast.success(

          becameSupported

            ? `${getLanguagePickerLabel(updated)} enabled. Slot provisioning and translation have been queued in the background.`

            : `${getLanguagePickerLabel(updated)} updated.`

        );

      } else {

        toast.error(apiMessage(resp, "Failed to update language."));

      }

    } catch (error) {

      console.error("Error updating language:", error);

      toast.error("Error updating language.");

    } finally {

      setSavingLanguageId(null);

      if (typeof updateSpinner === "function") updateSpinner(false);

    }

  }



  function onToggleSupported(language: ReturnType<typeof normalizeLanguage>) {

    if (language.id === ENGLISH_LANGUAGE_ID) return;

    void updateLanguage(language, { supported: !language.supported });

  }



  function onToggleActive(language: ReturnType<typeof normalizeLanguage>) {

    if (language.id === ENGLISH_LANGUAGE_ID) return;

    void updateLanguage(language, { active: !language.active });

  }



  function onDisplayDraftChange(languageId: number, value: string) {

    setDisplayDrafts((current) => ({ ...current, [languageId]: value }));

  }



  async function provisionLanguageSlots(language: ReturnType<typeof normalizeLanguage>) {

    if (language.id <= ENGLISH_LANGUAGE_ID || !language.supported) return;



    setSavingLanguageId(language.id);

    if (typeof updateSpinner === "function") updateSpinner(true);

    try {

      const resp = await RightTimeApi.postAsync(`/Languages/${language.id}/provision-slots`, {});

      if (resp?.ok && resp.jsonData) {

        const created = Number(resp.jsonData.totalCreated ?? resp.jsonData.TotalCreated ?? 0);

        const remaining = Number(

          resp.jsonData.remainingMissingSlotCount ?? resp.jsonData.RemainingMissingSlotCount ?? 0

        );

        const queuedCount = Number(

          resp.jsonData.queuedTranslationCount ?? resp.jsonData.QueuedTranslationCount ?? 0

        );

        const translationQueued = !!(resp.jsonData.translationQueued ?? resp.jsonData.TranslationQueued);

        const languageLabel = getLanguageRegistryName(language);

        if (translationQueued && queuedCount > 0) {

          toast.success(

            created > 0

              ? `Created ${created} content slot(s) and queued ${queuedCount} translation(s) for ${languageLabel}.`

              : `Queued ${queuedCount} translation(s) for ${languageLabel}.`

          );

        } else if (created > 0) {

          toast.success(`Created ${created} empty content slot(s) for ${languageLabel}.`);

        } else {

          toast.success(`No missing content slots for ${languageLabel}.`);

        }

        setLanguages((current) => current.map((item) => (

          item.id === language.id

            ? {

              ...item,

              missingSlotCount: Number.isFinite(remaining) ? remaining : 0,

            }

            : item

        )));

        await loadLanguages();

      } else {

        toast.error(apiMessage(resp, "Failed to provision language slots."));

      }

    } catch (error) {

      console.error("Error provisioning language slots:", error);

      toast.error("Error provisioning language slots.");

    } finally {

      setSavingLanguageId(null);

      if (typeof updateSpinner === "function") updateSpinner(false);

    }

  }



  async function translateLanguageSlots(language: ReturnType<typeof normalizeLanguage>) {

    if (language.id <= ENGLISH_LANGUAGE_ID || !language.supported) return;



    setSavingLanguageId(language.id);

    if (typeof updateSpinner === "function") updateSpinner(true);

    try {

      const resp = await RightTimeApi.postAsync(`/Languages/${language.id}/translate-slots`, {});

      if (resp?.ok && resp.jsonData) {

        const queuedCount = Number(

          resp.jsonData.queuedTranslationCount ?? resp.jsonData.QueuedTranslationCount ?? 0

        );

        const markedCount = Number(

          resp.jsonData.markedForTranslationCount ?? resp.jsonData.MarkedForTranslationCount ?? queuedCount

        );

        const fieldCreated = Number(
          resp.jsonData.fieldTranslationsCreated ?? resp.jsonData.FieldTranslationsCreated ?? 0
        );

        const fieldUpdated = Number(
          resp.jsonData.fieldTranslationsUpdated ?? resp.jsonData.FieldTranslationsUpdated ?? 0
        );

        const fieldTotal = fieldCreated + fieldUpdated;

        const message = apiMessage(resp) || null;

        toast.success(

          message

            ?? (fieldTotal > 0 && queuedCount > 0

              ? `Translated ${fieldTotal} field label(s) and queued ${queuedCount} content translation(s) for ${getLanguageRegistryName(language)}.`

              : fieldTotal > 0

                ? `Translated ${fieldTotal} field label(s) for ${getLanguageRegistryName(language)}.`

                : queuedCount > 0

                  ? `Queued ${queuedCount} translation(s) for ${getLanguageRegistryName(language)}.`

                  : markedCount > 0

                    ? `Marked ${markedCount} slot(s) for translation.`

                    : `No translation work remaining for ${getLanguageRegistryName(language)}.`)

        );

        await loadLanguages();

      } else {

        toast.error(apiMessage(resp, "Failed to start translation."));

      }

    } catch (error) {

      console.error("Error starting language translation:", error);

      toast.error("Error starting language translation.");

    } finally {

      setSavingLanguageId(null);

      if (typeof updateSpinner === "function") updateSpinner(false);

    }

  }



  async function resetLanguageTranslations(language: ReturnType<typeof normalizeLanguage>) {

    if (language.id <= ENGLISH_LANGUAGE_ID || !language.supported || language.active) return;



    const label = getLanguageRegistryName(language);

    if (!window.confirm(

      `Clear all translated content for ${label}? Existing translations for this language will be removed. Use Translate when you are ready to re-translate from English.`

    )) {

      return;

    }



    setSavingLanguageId(language.id);

    if (typeof updateSpinner === "function") updateSpinner(true);

    try {

      const resp = await RightTimeApi.postAsync(`/Languages/${language.id}/reset-translations`, {});

      if (resp?.ok && resp.jsonData) {

        const resetCount = Number(resp.jsonData.resetSlotCount ?? resp.jsonData.ResetSlotCount ?? 0);

        const message = apiMessage(resp);

        toast.success(message || (resetCount > 0

          ? `Cleared ${resetCount} translated slot(s) for ${label}.`

          : `No translated content to reset for ${label}.`));

        await loadLanguages();

      } else {

        toast.error(apiMessage(resp, "Failed to reset translations."));

      }

    } catch (error) {

      console.error("Error resetting language translations:", error);

      toast.error("Error resetting language translations.");

    } finally {

      setSavingLanguageId(null);

      if (typeof updateSpinner === "function") updateSpinner(false);

    }

  }



  async function deleteLanguage(language: ReturnType<typeof normalizeLanguage>) {

    if (!canDeleteLanguage(language) || savingLanguageId != null) return;

    const label = getLanguageRegistryName(language);

    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;



    setSavingLanguageId(language.id);

    if (typeof updateSpinner === "function") updateSpinner(true);

    try {

      const resp = await RightTimeApi.deleteAsync(`/Languages/${language.id}`);

      if (resp?.ok) {

        toast.success(`${label} deleted.`);

        await refreshFieldTranslations();

        await loadLanguages();

      } else {

        toast.error(apiMessage(resp, "Failed to delete language."));

      }

    } catch (error) {

      console.error("Error deleting language:", error);

      toast.error("Error deleting language.");

    } finally {

      setSavingLanguageId(null);

      if (typeof updateSpinner === "function") updateSpinner(false);

    }

  }



  function onDisplayBlur(language: ReturnType<typeof normalizeLanguage>) {

    const draft = String(displayDrafts[language.id] ?? "").trim();

    if (!draft) {

      setDisplayDrafts((current) => ({ ...current, [language.id]: language.display }));

      toast.error("Display cannot be empty.");

      return;

    }

    if (draft === language.display) return;

    void updateLanguage(language, { display: draft });

  }



  if (!isOnThisTab || !isSystemAdmin) return null;



  return (

    <>

      <div className="tableWrapper dimensionTable adminLanguagesTable scrollable">

        <table>

          <caption data-help-id="Languages">

            <span className="title">Languages ({languages.length})</span>

            <span className="captionRight">

              <FaPlusSquare

                className="actionIcon addLanguage"

                title="Add language from translator catalog"

                onClick={() => void openAddLanguageModal()}

              />

            </span>

          </caption>

          <thead>

            <tr>

              <th>ID</th>

              <th>Name</th>

              <th>Display</th>

              <th>IETF</th>

              <th>Abbrev</th>

              <th className="centerAlignedCell">Supported</th>

              <th className="centerAlignedCell">Active</th>

              <th className="centerAlignedCell">Slots</th>

              <th className="centerAlignedCell">Actions</th>

            </tr>

          </thead>

          <tbody>

            {loading && languages.length === 0 ? (

              <tr>

                <td colSpan={9} className="centerAlignedCell paddedCell">

                  Loading languages...

                </td>

              </tr>

            ) : languages.length === 0 ? (

              <tr>

                <td colSpan={9} className="centerAlignedCell emptyStateCell">

                  No languages found.

                </td>

              </tr>

            ) : (

              languages.map((language) => {

                const isEnglish = language.id === ENGLISH_LANGUAGE_ID;

                const isSaving = savingLanguageId === language.id;

                const supportedDisabled = isEnglish || isSaving;

                const activeDisabled = isEnglish || !language.supported || isSaving;

                const showProvision = shouldShowProvisionButton(language);

                const showTranslate = shouldShowTranslateButton(language);

                const showReset = shouldShowResetTranslationsButton(language);

                const showDelete = canDeleteLanguage(language);



                return (

                  <tr key={language.id}>

                    <td>{language.id}</td>

                    <td>{getLanguageRegistryName(language)}</td>

                    <td>

                      <input

                        type="text"

                        value={displayDrafts[language.id] ?? language.display}

                        maxLength={50}

                        disabled={isSaving}

                        onChange={(e) => onDisplayDraftChange(language.id, e.target.value)}

                        onBlur={() => onDisplayBlur(language)}

                        onKeyDown={(e) => {

                          if (e.key === "Enter") {

                            e.currentTarget.blur();

                          }

                        }}

                        title="Label shown to users in language pickers"

                      />

                    </td>

                    <td>{language.ietf || "—"}</td>

                    <td>{language.abbrev || "—"}</td>

                    <td

                      className={`centerAlignedCell${supportedDisabled ? "" : " clickableCell"}`}

                      onClick={() => !supportedDisabled && onToggleSupported(language)}

                      title={isEnglish ? "English is always supported" : "Toggle platform support"}

                    >

                      {language.supported ? (

                        <MdCheckBox className={`actionIcon${supportedDisabled ? " disabledDeleteIcon" : ""}`} />

                      ) : (

                        <MdCheckBoxOutlineBlank className={`actionIcon${supportedDisabled ? " disabledDeleteIcon" : ""}`} />

                      )}

                    </td>

                    <td

                      className={`centerAlignedCell${activeDisabled ? "" : " clickableCell"}`}

                      onClick={() => !activeDisabled && onToggleActive(language)}

                      title={

                        isEnglish

                          ? "English is always active for customers"

                          : !language.supported

                            ? "Enable Supported first"

                            : "Toggle customer-facing availability"

                      }

                    >

                      {language.active ? (

                        <MdCheckBox className={`actionIcon${activeDisabled ? " disabledDeleteIcon" : ""}`} />

                      ) : (

                        <MdCheckBoxOutlineBlank className={`actionIcon${activeDisabled ? " disabledDeleteIcon" : ""}`} />

                      )}

                    </td>

                    <td className="centerAlignedCell languageSlotsCell" title={getSlotsCellTitle(language)}>

                      {language.id === ENGLISH_LANGUAGE_ID ? (

                        <span className="languageSlotsStatus">Source</span>

                      ) : !language.supported ? (

                        <span className="languageSlotsStatus muted">—</span>

                      ) : (

                        <div className="languageSlotsActions">

                          {showProvision && (

                            <button

                              type="button"

                              className="actionIcon"

                              disabled={isSaving}

                              onClick={() => void provisionLanguageSlots(language)}

                              title={getSlotsCellTitle(language)}

                            >

                              Provision ({language.missingSlotCount ?? 0})

                            </button>

                          )}

                          {!showProvision && language.pendingTranslationCount != null && language.pendingTranslationCount > 0 ? (

                            <span className="languageSlotsStatus">

                              Translating ({language.pendingTranslationCount})

                            </span>

                          ) : showTranslate && (language.missingFieldTranslationCount ?? 0) > 0
                            && (language.missingSlotCount ?? 0) === 0
                            && (language.untranslatedSlotCount ?? 0) === 0 ? (

                            <span className="languageSlotsStatus">

                              Fields ({language.missingFieldTranslationCount})

                            </span>

                          ) : shouldShowAllocatedStatus(language) ? (

                            <span className="languageSlotsStatus">Translated</span>

                          ) : (

                            <span className="languageSlotsStatus muted">—</span>

                          )}

                        </div>

                      )}

                    </td>

                    <td className="centerAlignedCell">

                      <div className="languageRowActions">

                        {showTranslate ? (

                          <button

                            type="button"

                            className="actionIcon"

                            disabled={isSaving}

                            onClick={() => void translateLanguageSlots(language)}

                            title={getSlotsCellTitle(language)}

                          >

                            Translate ({getTranslationWorkCount(language)})

                          </button>

                        ) : null}

                        {showReset ? (

                          <button

                            type="button"

                            className="actionIcon languageResetButton"

                            disabled={isSaving}

                            title="Clear translated content for this language"

                            onClick={() => void resetLanguageTranslations(language)}

                          >

                            Reset

                          </button>

                        ) : null}

                        {showDelete ? (

                          <MdDelete

                            className={`actionIcon trashIcon${isSaving ? " disabledDeleteIcon" : ""}`}

                            title="Delete language"

                            onClick={() => !isSaving && void deleteLanguage(language)}

                          />

                        ) : null}

                      </div>

                    </td>

                  </tr>

                );

              })

            )}

          </tbody>

        </table>

      </div>



      <SelectModal

        isOpen={showAddLanguageModal}

        title="Add Language"

        message={loadingTranslatorLanguages ? "Loading available languages..." : "Select a language to add:"}

        caption="Translator Languages"

        items={availableTranslatorLanguages}

        itemLabelKey="label"

        itemValueKey="code"

        selectText="Add"

        onSelect={(item) => void addLanguage(item)}

        onCancel={() => setShowAddLanguageModal(false)}

      />

    </>

  );

}

